import axios from "axios"
import { kratosClient } from "../libs/kratos/client"
import { KratosSession } from "../libs/kratos/types"
import { authStorageAdapter } from "../libs/auth/storageAdapter"
import { parseKratosError } from "../libs/kratos/errors"
import { isDesktop, isElectron, isTauri, DesktopAuth } from "../libs/desktop"

export interface LoginResult {
  sessionToken: string
  session: KratosSession
}

export class AuthService {
  private static activeCheckPromise: Promise<KratosSession | null> | null = null
  private static cachedSession: {
    session: KratosSession | null
    expiresAt: number
  } | null = null
  private static readonly SESSION_CACHE_TTL_MS = 3000 // 3 секунды для предотвращения дублирующих запросов

  /**
   * Сбрасывает локальный кэш сессии AuthService.
   */
  static clearCache(): void {
    this.cachedSession = null
    this.activeCheckPromise = null
  }

  /**
   * Проверяет, запущено ли приложение в Electron Desktop оболочке.
   */
  static isElectronEnvironment(): boolean {
    return isElectron()
  }

  /**
   * Проверяет, запущено ли приложение в Tauri Desktop оболочке.
   */
  static isTauriEnvironment(): boolean {
    return isTauri()
  }

  /**
   * Проверяет, запущено ли приложение в десктопной оболочке (Electron или Tauri).
   */
  static isDesktopEnvironment(): boolean {
    return isDesktop()
  }

  /**
   * Универсальный вход в систему:
   * - В Desktop (Electron/Tauri): Native API Flow (без Origin headers, безопасное хранилище OS).
   * - В Web Browser: Browser Flow через Kratos с CSRF-токеном и Same-Origin/CORS cookies.
   */
  static async login(identifier: string, pass: string): Promise<LoginResult> {
    this.clearCache()
    if (this.isDesktopEnvironment()) {
      return this.loginDesktop(identifier, pass)
    }
    return this.loginBrowser(identifier, pass)
  }

  /**
   * Desktop Flow: выполнение через системный процесс (Electron IPC или Tauri Invoke).
   */
  private static async loginDesktop(
    identifier: string,
    pass: string
  ): Promise<LoginResult> {
    try {
      const res = await DesktopAuth.login(identifier, pass)

      if (!res?.success) {
        const errPayload = res?.error as any
        const errorPayload = errPayload?.ui
          ? { response: { data: res.error } }
          : {
              response: { data: res?.error },
              message: errPayload?.error?.message || "Ошибка входа",
            }

        const parsed = parseKratosError(errorPayload)
        const error = new Error(parsed.generalMessage)
        ;(error as any).fieldErrors = parsed.fieldErrors
        ;(error as any).originalError = res?.error
        throw error
      }

      const token = res.sessionToken || res.session_token || ""
      if (token) {
        await authStorageAdapter.saveToken(token)
      }

      const session = res.session as KratosSession
      this.cachedSession = {
        session,
        expiresAt: Date.now() + this.SESSION_CACHE_TTL_MS,
      }

      return {
        sessionToken: token,
        session,
      }
    } catch (err: any) {
      if (err.fieldErrors) {
        throw err
      }
      const parsed = parseKratosError(err)
      const error = new Error(parsed.generalMessage)
      ;(error as any).fieldErrors = parsed.fieldErrors
      ;(error as any).originalError = err
      throw error
    }
  }

  private static async loginElectron(
    identifier: string,
    pass: string
  ): Promise<LoginResult> {
    return this.loginDesktop(identifier, pass)
  }

  /**
   * Web Flow: выполнение через Kratos Browser Flow с CSRF и cookies.
   */
  private static async loginBrowser(
    identifier: string,
    pass: string
  ): Promise<LoginResult> {
    try {
      // 1. Инициализация Browser Login Flow (устанавливает csrf_token cookie)
      const initFlow = await kratosClient.createBrowserLoginFlow(undefined, {
        withCredentials: true,
      })
      const flowId = initFlow.data.id

      // 2. Извлечение csrf_token из UI-узлов
      const csrfNode = initFlow.data.ui.nodes.find(
        (node: any) => (node.attributes as any)?.name === "csrf_token"
      )
      const csrfToken = (csrfNode?.attributes as any)?.value || ""

      // 3. Отправка учетных данных с csrf_token и withCredentials: true
      const res = await kratosClient.updateLoginFlow(
        {
          flow: flowId,
          updateLoginFlowBody: {
            method: "password",
            identifier,
            password: pass,
            csrf_token: csrfToken,
          },
        },
        { withCredentials: true }
      )

      const session = res.data.session as unknown as KratosSession
      if (!session) {
        throw new Error("Не удалось получить данные сессии от Kratos")
      }

      // В Browser Flow Kratos выставляет cookie ory_kratos_session.
      // Сохраняем session_token только если Kratos вернул реальный токен сессии (ory_st_...).
      // Не сохраняем UUID session.id в качестве Bearer-токена.
      const sessionToken = res.data.session_token
      if (
        sessionToken &&
        typeof sessionToken === "string" &&
        sessionToken.startsWith("ory_st_")
      ) {
        await authStorageAdapter.saveToken(sessionToken)
      } else {
        await authStorageAdapter.clearToken()
      }

      this.cachedSession = {
        session,
        expiresAt: Date.now() + this.SESSION_CACHE_TTL_MS,
      }

      return {
        sessionToken: sessionToken || "",
        session,
      }
    } catch (err: any) {
      if (
        err.response?.data?.error?.id === "session_already_available" ||
        (err.response?.status === 400 &&
          String(err.response?.data?.error?.message || "")
            .toLowerCase()
            .includes("already logged in"))
      ) {
        const session = await this.checkSession()
        if (session) {
          const token = await authStorageAdapter.getToken()
          const sessionToken = token && token.startsWith("ory_st_") ? token : ""
          return {
            sessionToken,
            session,
          }
        }
      }

      const parsed = parseKratosError(err)
      const error = new Error(parsed.generalMessage)
      ;(error as any).fieldErrors = parsed.fieldErrors
      ;(error as any).originalError = err
      throw error
    }
  }

  /**
   * Проверяет валидность текущей сессии:
   * - В Electron: whoami через IPC Main Process с X-Session-Token.
   * - В Web: whoami через cookie ory_kratos_session или X-Session-Token.
   *
   * Использует дедупликацию одновременных вызовов (in-flight promise)
   * и кратковременный кэш (TTL 3 сек), предотвращая множественные запросы к /sessions/whoami.
   */
  static async checkSession(
    forceRefresh = false
  ): Promise<KratosSession | null> {
    const now = Date.now()
    if (
      !forceRefresh &&
      this.cachedSession &&
      this.cachedSession.expiresAt > now
    ) {
      return this.cachedSession.session
    }

    if (this.activeCheckPromise) {
      return this.activeCheckPromise
    }

    this.activeCheckPromise = (async () => {
      try {
        const session = await this.performCheckSession()
        this.cachedSession = {
          session,
          expiresAt: Date.now() + this.SESSION_CACHE_TTL_MS,
        }
        return session
      } finally {
        this.activeCheckPromise = null
      }
    })()

    return this.activeCheckPromise
  }

  /**
   * Выполняет непосредственный сетевой запрос проверки сессии.
   */
  private static async performCheckSession(): Promise<KratosSession | null> {
    try {
      if (this.isDesktopEnvironment()) {
        const session = (await DesktopAuth.whoami()) as KratosSession | null
        if (session && session.active) {
          return session
        }
        await authStorageAdapter.clearToken()
        return null
      }

      // Web Browser Flow
      const token = await authStorageAdapter.getToken()

      const res = await kratosClient.toSession(
        token && token.startsWith("ory_st_")
          ? { xSessionToken: token }
          : undefined,
        { withCredentials: true }
      )

      if (res.status === 200 && res.data?.active) {
        const session = res.data as unknown as KratosSession
        const sessionToken = (res.data as any).session_token
        if (
          sessionToken &&
          typeof sessionToken === "string" &&
          sessionToken.startsWith("ory_st_")
        ) {
          await authStorageAdapter.saveToken(sessionToken)
        } else {
          // В Cookie-flow не сохраняем UUID session.id как Bearer-токен
          await authStorageAdapter.clearToken()
        }
        return session
      }

      await authStorageAdapter.clearToken()
      return null
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        await authStorageAdapter.clearToken()
      }
      return null
    }
  }

  /**
   * Завершает сессию кассира в Kratos и очищает локальное хранилище.
   */
  static async logout(): Promise<void> {
    try {
      if (this.isDesktopEnvironment()) {
        await DesktopAuth.logout()
        return
      }

      // Web Browser Flow
      const token = await authStorageAdapter.getToken()
      if (token && token.startsWith("ory_st_")) {
        await kratosClient.performNativeLogout(
          {
            performNativeLogoutBody: {
              session_token: token,
            },
          },
          { withCredentials: true }
        )
      } else {
        const flow = await kratosClient.createBrowserLogoutFlow(undefined, {
          withCredentials: true,
        })
        if (flow.data?.logout_url) {
          await axios.get(flow.data.logout_url, { withCredentials: true })
        }
      }
    } catch (err) {
      console.warn("[AuthService]: Error during Kratos logout:", err)
    } finally {
      this.clearCache()
      await authStorageAdapter.clearToken()
    }
  }
}
